<template>
  <div
    class="sFormItem"
    :class="classes">
    <template v-if="isTableCell">
      <transition
        name="s-zoom-in-center">
        <sTooltip
          v-if="validateState==='error'"
          :border="false"
          :content="validateMessage"
          type="error">
          <sIcon
            class="s-cell__validateIcon"
            type="info"/>
        </sTooltip>
      </transition>
    </template>
    <template v-else>
      <label-wrap
        :is-auto-width="labelStyle && labelStyle.width === 'auto'"
        :update-all="form.labelWidth === 'auto'">
        <label
          v-if="label || $slots.label"
          class="sFormItem__label"
          :style="labelStyle">
          <slot name="label">{{ label + (form.labelSuffix || '') }}
            <slot name="label-after"/>
          </slot>
        </label>
      </label-wrap>
      <div
        class="sFormItem__content"
        :style="contentStyle">
        <slot
          :error="validateMessage"
          :state="validateState"/>
        <slot name="before"/>
        <transition name="s-zoom-in-top">
          <slot
            v-if="showErrorMessage"
            :error="validateMessage"
            name="error">
            <div
              class="sFormItem__error"
              :class="{
                'sFormItem__error--inline': typeof inlineMessage === 'boolean'
                  ? inlineMessage
                  : (form && form.inlineMessage || false)}">
              {{ validateMessage }}
            </div>
          </slot>
        </transition>
      </div>
    </template>
  </div>
</template>
<script>
import AsyncValidator from 'async-validator'
import emitter from '../../mixins/emitter'
import objectAssign from '../../utils/merge'
import LabelWrap from './labelWrap'
export default {
  name: 'sFormItem',
  componentName: 'sFormItem',
  mixins: [emitter],
  provide() {
    return {
      sFormItem: this
    }
  },
  inject: {
    sForm: {
      default: '',
    },
  },
  data() {
    return {
      validateState: '',
      validateMessage: '',
      validateDisabled: false,
      validator: {},
      computedLabelWidth: ''
    }
  },
  props: {
    /**
     * метка
     */
    label: {
      type: String,
    },
    /**
     * ширина этикетки, например '50px. Поддерживается автоматическая ширина.
     */
    labelWidth: {
      type: String,
    },
    /**
     * ключ модели. При использовании методов validate и resetFields атрибут является обязательным.
     */
    prop: {
      type: String,
    },
    /**
     * является ли поле обязательным или нет, будет определено правилами проверки, если не указанно
     */
    required: {
      type: Boolean,
      default: undefined
    },
    /**
     * правила проверки формы
     */
    rules: {
      type: [Object, Array],
    },
    /**
     * сообщение об ошибке поля, установите значение, и поле подтвердит ошибку и немедленно покажет это сообщение
     */
    error: {
      type: String,
    },
    validateStatus: {
      type: String,
    },
    /**
     * @ignore
     */
    for: {
      type: String,
    },
    /**
     * inline стиль сообщение проверки
     */
    inlineMessage: {
      type: [String, Boolean],
      default: ''
    },
    /**
     * показывать ли сообщение об ошибке
     */
    showMessage: {
      type: Boolean,
      default: true
    },
    /**
     * стиль строки с эллементом
     */
    itemStyle: {
      type: String,
    },
    mode: {
      type: String,
      default: 'component',
      validator: function (value) {
        return ['component', 'table-cell'].indexOf(value) !== -1
      },
    }
  },
  components: {
    LabelWrap,
  },
  watch: {
    error: {
      immediate: true,
      handler(value) {
        this.validateMessage = value
        this.validateState = value ? 'error' : ''
      }
    },
    validateStatus(value) {
      this.validateState = value
    },
    mode: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$nextTick(() => {
          this.setParrentClass('sTable-cell-valid', value === 'table-cell')
        })
      }
    }
  },
  computed: {
    showErrorMessage() {
      return this.validateState == 'error'
      && this.showMessage
      && (
        this.$parent?.$options?.componentName !== 'sForm'
        || this.form.showMessage
      )
    },
    classes() {
      if (this.isTableCell) {
        return [ 'is-hide-message', 'is-table-cell' ]
      }
      return {
        'is-error': this.validateState === 'error',
        'is-validating': this.validateState === 'validating',
        'is-success': this.validateState === 'success',
        'is-required': this.isRequired || this.required,
        'is-no-asterisk': this.form && this.form.hideRequiredAsterisk,
        'is-hide-message': !this.showMessage || (this.form && !this.form.showMessage)
      }
    },
    isTableCell() {
      return this.mode === 'table-cell'
    },
    labelStyle() {
      if (!this.form) { return null }
      const ret = {}
      if (this.form.labelPosition === 'top') { return ret }
      const labelWidth = this.labelWidth || this.form.labelWidth
      if (labelWidth) {
        ret.width = labelWidth
      }
      return ret
    },
    isNested() {
      let parentName = this.form?.$options?.componentName
      return parentName !== 'sForm' && parentName === 'sFormItem'
    },
    contentStyle() {
      if (!this.form) { return null }
      const ret = {}
      const label = this.label

      if (this.form.labelPosition === 'top' || this.form.inline) { return ret }
      if (!label && !this.labelWidth && this.isNested) { return ret }
      const labelWidth = this.labelWidth || this.form.labelWidth
      if (labelWidth === 'auto') {
        if (this.labelWidth === 'auto') {
          ret.marginLeft = this.computedLabelWidth
        } else if (this.form.labelWidth === 'auto') {
          ret.marginLeft = this.form.autoLabelWidth
        }
      } else {
        ret.marginLeft = labelWidth || 'auto'
      }
      return ret
    },
    form() {
      let parent = this.$parent
      if (!parent) {
        return {}
      }
      let parentName = parent.$options?.componentName
      while (parentName !== 'sForm') {
        parent = parent.$parent
        if (!parent) { return {} }
        parentName = parent.$options?.componentName
      }
      return parent
    },
    fieldValue() {
      if (!this.form) { return }
      const model = this.form.model
      if (!model || !this.prop) { return }
      let path = this.prop
      if (path.indexOf(':') !== -1) {
        path = path.replace(/:/, '.')
      }
      return this.getPropByPath(model, path, true).v
    },
    isRequired() {
      let rules = this.getRules()
      let isRequired = false
      if (rules && rules.length) {
        rules.every(rule => {
          if (rule.required) {
            isRequired = true
            return false
          }
          return true
        })
      }
      return isRequired
    },
  },
  methods: {
    validateAsync(onlyIfChange = false, trigger) {
      return new Promise((resolve) => {
        const callback = (msg, invalidFields) => {
          resolve({
            valid: !msg,
            message: msg,
            invalidFields
          })
        }
        const result = this.validate(onlyIfChange, trigger, callback)
        if (result === true) {
          resolve({ valid: true, message: '', invalidFields: undefined })
        }
      })
    },
    setParrentClass(className, add = true) {
      if (this.$el && this.$el.parentElement) {
        if (add) {
          this.$el.parentElement.classList.add(className)
        } else {
          this.$el.parentElement.classList.remove(className)
        }
      }
    },
    setTdStyle() {
      this.setParrentClass('sForm-cell-error', this.validateState === 'error')
    },
    getPropByPath(obj, path, strict) {
      let tempObj = obj
      path = path.replace(/\[(\w+)\]/g, '.$1')
      path = path.replace(/^\./, '')

      let keyArr = path.split('.')
      let i = 0
      for (let len = keyArr.length; i < len - 1; ++i) {
        if (!tempObj && !strict) { break }
        let key = keyArr[i]
        if (tempObj && key in tempObj) {
          tempObj = tempObj[key]
        } else {
          if (strict) {
            /*if (window?.config?.node_env === 'development') {
              throw new Error(`Проверьте путь к свойству! path: ${path} key: ${key}`)
            } else {*/
            return {
              o: '',
              k: {},
              v: undefined
            }
            //}
          }
          break
        }
      }
      return {
        o: tempObj,
        k: keyArr[i],
        v: tempObj ? tempObj[keyArr[i]] : null
      }
    },
    isChange() {
      return this.initialValue !== this.fieldValue
    },
    validate(onlyIfChange = false, trigger, callback = {}) {
      if (onlyIfChange && !this.isChange()) { return true }
      this.validateDisabled = false
      const rules = this.getFilteredRule(trigger)
      if ((!rules || rules.length === 0) && this.required === undefined) {
        if (callback && typeof callback === 'function') { callback() }

        if (this.isTableCell) { this.setTdStyle() }
        return true
      }
      this.validateState = 'validating'
      const descriptor = {}
      if (rules && rules.length > 0) {
        rules.forEach(rule => {
          delete rule.trigger
        })
      }
      descriptor[this.prop] = rules
      const validator = new AsyncValidator(descriptor)
      const model = {}
      model[this.prop] = this.fieldValue
      validator.validate(model, { firstFields: true }, (errors, invalidFields) => {
        this.validateState = !errors ? 'success' : 'error'
        this.validateMessage = errors ? errors[0].message : ''
        if (callback && typeof callback === 'function') { callback(this.validateMessage, invalidFields) }
        //callback(this.validateMessage, invalidFields)
        this.sForm && this.sForm.$emit('validate', this.prop, !errors, this.validateMessage || null)
      })

      if (this.isTableCell) { this.setTdStyle() }
    },
    /**
     * удалить статус проверки поля
     */
    clearValidate() {
      this.validateState = ''
      this.validateMessage = ''
      this.validateDisabled = false

      if (this.isTableCell) { this.setTdStyle() }
    },
    /**
     * сбросить текущее поле и удалить результат проверки
     */
    resetField() {
      if (!this.form) { return }
      this.validateState = ''
      this.validateMessage = ''
      let model = this.form.model
      let value = this.fieldValue
      let path = this.prop
      if (path.indexOf(':') !== -1) {
        path = path.replace(/:/, '.')
      }
      let prop = this.getPropByPath(model, path, true)
      this.validateDisabled = true
      if (Array.isArray(value)) {
        prop.o[prop.k] = [].concat(this.initialValue)
      } else {
        prop.o[prop.k] = this.initialValue
      }
      this.$nextTick(() => {
        this.validateDisabled = false
      })
      this.broadcast('ElTimeSelect', 'fieldReset', this.initialValue)
    },
    getRules() {
      if (!this.form) { return }
      let formRules = this.form.rules
      const selfRules = this.rules
      const requiredRule = this.required !== undefined ? { required: !!this.required } : []
      const prop = this.getPropByPath(formRules, this.prop || '')
      formRules = formRules ? (prop.o[this.prop || ''] || prop.v) : []
      return [].concat(selfRules || formRules || []).concat(requiredRule)
    },
    getFilteredRule(trigger) {
      const rules = this.getRules()

      if (trigger === undefined) { return rules }
      return rules.filter(rule => {
        if (!rule.trigger || trigger === '') { return true }
        if (Array.isArray(rule.trigger)) {
          return rule.trigger.indexOf(trigger) > -1
        } else {
          return rule.trigger === trigger
        }
      }).map(rule => objectAssign({}, rule))
    },
    onFieldBlur() {
      this.validate('blur')
    },
    onFieldChange() {
      if (this.validateDisabled) {
        this.validateDisabled = false
        return
      }
      this.validate('change')
    },
    updateComputedLabelWidth(width) {
      this.computedLabelWidth = width ? `${width}px` : ''
    },
    addValidateEvents() {
      const rules = this.getRules()
      if (rules && (rules.length || this.required !== undefined)) {
        this.$on('s.form.blur', this.onFieldBlur)
        this.$on('s.form.change', this.onFieldChange)
      }
    },
    removeValidateEvents() {
      this.$off()
    }
  },
  mounted() {
    if (this.prop) {
      this.dispatch('sForm', 's.form.addField', [this])
      let initialValue = this.fieldValue
      if (Array.isArray(initialValue)) {
        initialValue = [].concat(initialValue)
      }
      Object.defineProperty(this, 'initialValue', {
        value: initialValue
      })
      this.addValidateEvents()
    }
  },
  beforeDestroy() {
    this.dispatch('sForm', 's.form.removeField', [this])
  }
}
</script>
